<template>
  <div class="fill-height" :style="cssVars">
    <!-- The tabs - controls the active tab state -->
    <tabs
      v-model="selectedIndexModel"
      class="tab-items"
      :class="`pl-${leftPad} pt-${topPad} pb-${bottomPad}`"
    >
      <!-- The tab items -->
      <v-item
        v-for="tab in tabs"
        :key="tab.label"
        v-slot="{ toggle, active }"
        class="sub-page-tab-item"
        :class="{ ['share-width']: shareWidth }"
      >
        <!-- The tab button -->
        <v-btn
          text
          :color="active ? 'primary' : undefined"
          @click="toggle"
        >
          <!-- The tab label -->
          <span class="text text-none" :class="{ active }">
            {{ tab.label }}
          </span>
          <!-- The tab count chip -->
          <v-chip x-small class="chip ml-2 px-1" :class="{ active }">
            {{ (tab.count || 0).toLocaleString() }}
          </v-chip>
        </v-btn>
      </v-item>

      <!-- Slot to add additional content to the right of the tabs -->
      <slot name="append-right"></slot>
    </tabs>
    <!-- The tab panels -->
    <div class="fill-remaining-height">
      <slot name="prepend-content"></slot>
      <!-- This controls which panel is showing based on the selectedIndex -->
      <v-tabs-items
        class="transparent fill-height"
        :value="selectedIndexModel"
      >
        <slot></slot>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import Tabs from './Tabs';

export default {
  name: 'SubPageTabs',
  components: { Tabs },
  props: {
    tabs: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every((tab) => {
          return tab.label;
        });
      },
    },
    selectedIndex: {
      type: Number,
      default: 0,
    },
    shareWidth: {
      type: Boolean,
      default: false,
    },
    leftPad: {
      type: [Number, String],
      default: 9,
    },
    topPad: {
      type: [Number, String],
      default: 0,
    },
    bottomPad: {
      type: [Number, String],
      default: 0,
    },
    height: {
      type: [Number, String],
      default: 38,
    },
    useHashState: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:selectedIndex'],
  computed: {
    selectedIndexModel: {
      get: function () {
        if (this.useHashState) {
          const hash = this.$route.hash;
          const index = this.tabs.findIndex((tab) => {
            return tab.hash === hash;
          });
          return index >= 0 ? index : this.selectedIndex;
        }
        return this.selectedIndex;
      },
      set: function (value) {
        if (this.useHashState) {
          const tab = this.tabs[value];
          if (tab) {
            window.location.hash = tab.hash;
          }
        }
        this.$emit('update:selectedIndex', value);
      },
    },
    cssVars() {
      return {
        '--tabs-height': `${this.height}px`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.fill-remaining-height {
  height: calc(100% - var(--tabs-height));
}

.tab-items {
  background-color: white;
  border-top: 1px solid map-get($grey, 'lighten-2');
  border-bottom: 1px solid map-get($grey, 'lighten-2');

  align-items: center;
}

.sub-page-tab-item {
  // Have all tabs take up equal space if fill is true.
  &.share-width {
    flex-grow: 1;
  }

  height: var(--tabs-height) !important;

  .text {
    // Make the tab text bold.
    font-weight: 700;

    // Make the inactive tab text grey.
    color: map-get($grey, 'base');

    // Make the active tab text primary.
    &.active {
      color: var(--v-primary-base);
    }
  }

  .chip {
    // Make the chip text bold.
    font-weight: 700;

    // Make the inactive chip text dark grey.
    color: map-get($grey, 'darken-2');

    // Make the active chip background primary & text white.
    &.active {
      background-color: var(--v-primary-base);
      color: white;
    }
  }
}
</style>
